import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, CalendarDays, CheckCircle2, LineChart,
  MessagesSquare, Send, Shield, Sparkles,
} from "lucide-react";
import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";
import api from "@/lib/api";
import { FadeUp, Stagger, StaggerItem, CountUp } from "@/components/common/Motion";
import {
  Layer, Magnetic, Marquee, Parallax, PointerScene, ScrollLift, TiltCard,
} from "@/components/common/Motion3D";
import heroPhoto from "@/assets/photos/marketing-team.jpg";
import teamPhoto from "@/assets/photos/workspace-planning.jpg";

// Taglines are marketing copy and live here; everything factual -- price,
// limits, what is included -- comes from the API, so this page cannot
// advertise a plan the product does not actually sell.
// Set REACT_APP_CONTACT_EMAIL before launch; the fallback is deliberately
// obvious rather than a plausible-looking address nobody reads.
const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || "hello@example.com";

const TAGLINES = {
  standard: "For one person getting started.",
  professional: "For growing teams publishing everywhere.",
  advanced: "For agencies that need approvals and audit.",
  enterprise: "For organisations that need control.",
};

// Rendered if the API cannot be reached. A marketing page that shows nothing
// because the backend is down is worse than one showing the current shape.
const FALLBACK_PLANS = [
  { code: "standard", name: "Standard", price: 29, popular: false,
    features: ["1 seat", "10 social accounts", "Unlimited scheduling", "Basic analytics"] },
  { code: "professional", name: "Professional", price: 99, popular: true,
    features: ["Unlimited social accounts", "Best-time recommendations", "Custom performance reports"] },
  { code: "advanced", name: "Advanced", price: 249, popular: false,
    features: ["Content approval workflows", "Multi-step approvals", "Team roles", "Audit log"] },
  { code: "enterprise", name: "Enterprise", price: null, popular: false,
    features: ["Advanced analytics", "SSO / SAML", "Data residency", "Dedicated support"] },
];

function describeLimits(plan) {
  const accounts =
    plan.max_social_accounts === -1
      ? "Unlimited social accounts"
      : `${plan.max_social_accounts} social accounts`;
  const seats =
    plan.max_seats === -1
      ? "Unlimited seats"
      : `${plan.max_seats} ${plan.max_seats === 1 ? "seat" : "seats"}`;
  return [seats, accounts];
}

function toCard(plan) {
  return {
    code: plan.code,
    name: plan.name,
    price: plan.is_custom_priced ? null : Math.round(plan.price_monthly / 100),
    popular: !!plan.is_popular,
    trialDays: plan.trial_days || 0,
    features: [...describeLimits(plan), ...(plan.features || [])],
  };
}

const FEATURES = [
  { icon: Send, title: "Compose once, publish everywhere", body: "Adapt each post per network with live previews for Instagram, LinkedIn, Facebook, and X." },
  { icon: Sparkles, title: "Caption & hashtag suggestions", body: "Get on-brand starting points tuned to each platform's tone and length limits." },
  { icon: CheckCircle2, title: "Approval workflow", body: "Editors submit, approvers review with comments, and only approved posts go out." },
  { icon: CalendarDays, title: "Schedule at the best time", body: "A calendar you can drag, and best-time recommendations based on real engagement." },
  { icon: LineChart, title: "Clear analytics", body: "Reach, engagement, clicks, and follower growth per post and per network — no clutter." },
  { icon: Shield, title: "Enterprise-ready", body: "Multi-tenant from day one. Roles, audit logs, and encryption baked in." },
];

const NETWORKS = ["Instagram", "LinkedIn", "Facebook", "X", "Threads", "TikTok", "YouTube", "Pinterest"];

export default function Landing() {
  const [plans, setPlans] = useState(FALLBACK_PLANS);

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/plans")
      .then(({ data }) => {
        if (cancelled || !data?.items?.length) return;
        setPlans(data.items.map(toCard));
      })
      .catch(() => {
        /* Keep the fallback shape rather than blanking the page. */
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="bg-white">
      <MarketingHeader />

      {/* ------------------------------------------------------------------
          Hero

          One three-dimensional scene rather than several: the whole group
          turns together around a single vanishing point, so the cards read as
          objects at different distances instead of as a dozen separately
          wobbling rectangles.
         ------------------------------------------------------------------ */}
      <section className="hs-mint-gradient relative overflow-hidden">
        <div className="hs-aurora" aria-hidden="true">
          <span className="a1" /><span className="a2" /><span className="a3" />
        </div>
        <div className="hs-grid-floor" aria-hidden="true" />
        <div className="hs-hero-glow absolute inset-0" />

        <div className="relative mx-auto max-w-7xl px-6 pb-24 pt-16 md:pb-32 md:pt-24">
          <FadeUp className="mx-auto max-w-3xl text-center">
            <span className="hs-tag" data-testid="hero-tag">
              <span className="h-1.5 w-1.5 rounded-full bg-[#16A46B]" />
              New — best-time scheduling in every plan
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-[#0B2B33] sm:text-5xl lg:text-6xl">
              Think Bigger. Post Smarter.<br />Grow Faster.
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base text-[#43535E] md:text-lg">
              Draft once, adapt per network, approve, and schedule — all in one clean workspace built for teams and agencies.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Magnetic>
                <Link to="/signup" data-testid="hero-primary-cta" className="hs-btn-primary inline-flex items-center gap-2">
                  Start 14-day trial <ArrowRight className="h-4 w-4" />
                </Link>
              </Magnetic>
              <a href={`mailto:${CONTACT_EMAIL}?subject=SocialMind demo request`}
                 data-testid="hero-secondary-cta" className="hs-underline-cta">Request a demo</a>
            </div>
            <p className="mt-4 text-xs text-[#8A97A0]">No credit card. Cancel any time.</p>
          </FadeUp>

          <PointerScene className="mx-auto mt-16 max-w-5xl" strength={7}>
            <div className="relative" style={{ transformStyle: "preserve-3d" }}>

              {/* The product itself, furthest back -- the thing the rest of
                  the scene is arranged around. */}
              <Layer z={0} className="rounded-2xl border border-[#E4E9EF] bg-white p-4 hs-depth-shadow hs-edge-light">
                <MockDashboard />
              </Layer>

              {/* Cards nearer the viewer. They swing further than the panel
                  behind them as the scene turns, which is the whole cue.
                  Hidden on narrow screens, where they would land on top of
                  the dashboard rather than beside it. */}
              <Layer
                z={90} float={10} delay={0.4}
                className="pointer-events-none absolute -left-6 top-16 hidden w-52 rounded-xl border border-[#E4E9EF] bg-white/95 p-4 backdrop-blur hs-depth-shadow lg:block"
              >
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A97A0]">Approved</div>
                <div className="mt-2 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#16A46B]" />
                  <span className="text-sm font-semibold text-[#0B2B33]">Autumn launch</span>
                </div>
                <div className="mt-1 text-[11px] text-[#8A97A0]">by Alice · 2 min ago</div>
              </Layer>

              <Layer
                z={130} float={14} delay={1.1}
                className="pointer-events-none absolute -right-4 top-6 hidden w-48 rounded-xl border border-[#E4E9EF] bg-white/95 p-4 backdrop-blur hs-depth-shadow lg:block"
              >
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A97A0]">Reach this week</div>
                <div className="mt-1 font-display text-2xl font-bold text-[#0B2B33]">
                  <CountUp to={184941} format={(n) => `${(n / 1000).toFixed(1)}k`} />
                </div>
                <div className="mt-1 text-[11px] font-semibold text-[#16A46B]">+18% vs. last week</div>
              </Layer>

              <Layer
                z={70} float={9} delay={1.8}
                className="pointer-events-none absolute -right-10 bottom-10 hidden w-44 rounded-xl border border-[#E4E9EF] bg-white/95 p-3 backdrop-blur hs-depth-shadow xl:block"
              >
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A97A0]">Publishing now</div>
                <div className="mt-2 flex items-center gap-2">
                  <motion.span
                    className="h-2 w-2 rounded-full bg-[#1F5FD8]"
                    animate={{ opacity: [1, 0.25, 1] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <span className="text-xs font-semibold text-[#0B2B33]">4 networks</span>
                </div>
              </Layer>
            </div>
          </PointerScene>
        </div>
      </section>

      {/* Networks, drifting past */}
      <section className="border-y border-[#E4E9EF] bg-white py-8">
        <p className="mb-5 text-center text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">
          Publish to every network your team manages
        </p>
        <Marquee duration={38}>
          {NETWORKS.map((n) => (
            <span key={n} className="rounded-full border border-[#E4E9EF] bg-[#F6F8FA] px-5 py-2 text-sm font-semibold text-[#43535E]">
              {n}
            </span>
          ))}
        </Marquee>
      </section>

      {/* Features — each card turns under the cursor on its own */}
      <section id="features" className="relative mx-auto max-w-7xl px-6 py-20 md:py-24">
        <FadeUp className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#1F5FD8]">Features</div>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#0B2B33] md:text-4xl">
            Everything your team needs. Nothing you don't.
          </h2>
        </FadeUp>

        <Stagger className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <StaggerItem key={f.title} className="h-full">
              <div style={{ perspective: "1000px" }} className="h-full">
                <TiltCard
                  strength={7}
                  data-testid={`feature-card-${f.title.split(" ")[0].toLowerCase()}`}
                  className="h-full rounded-xl border border-[#E4E9EF] bg-white p-6 hs-card-shadow"
                >
                  <div
                    className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#EEF3FB] text-[#1F5FD8]"
                    style={{ transform: "translateZ(30px)" }}
                  >
                    <f.icon className="h-5 w-5" />
                  </div>
                  {/* Lifted off the card face, so the text parts company with
                      its own background as the card turns. */}
                  <h3 className="font-display text-lg font-semibold text-[#0B2B33]" style={{ transform: "translateZ(18px)" }}>
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#43535E]" style={{ transform: "translateZ(10px)" }}>
                    {f.body}
                  </p>
                </TiltCard>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* A real team, moving against the scroll */}
      <section className="relative overflow-hidden border-y border-[#E4E9EF] bg-[#F6F8FA]">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 py-20 md:grid-cols-2 md:py-24">
          <ScrollLift>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#1F5FD8]">How it works</div>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#0B2B33] md:text-4xl">
              Draft, review, publish — without the group chat.
            </h2>
            <p className="mt-4 text-[#43535E]">
              Everything one team needs to agree on a post lives in one place: the draft, the
              per-network wording, who approved it, and when it went out.
            </p>
            <ul className="mt-6 space-y-3">
              {["Per-network drafts with live previews",
                "Approvals with comments and an audit trail",
                "One calendar you can drag"].map((line) => (
                <li key={line} className="flex items-start gap-2 text-sm text-[#43535E]">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#16A46B]" />
                  {line}
                </li>
              ))}
            </ul>
          </ScrollLift>

          <Parallax speed={0.12}>
            <div style={{ perspective: "1200px" }}>
              <TiltCard strength={6} className="overflow-hidden rounded-2xl border border-[#E4E9EF] hs-depth-shadow">
                {/* Decorative alongside the copy, which already carries the
                    meaning, so an empty alt rather than a repeat of it. */}
                <img
                  src={heroPhoto}
                  alt=""
                  width={1200}
                  height={800}
                  loading="lazy"
                  className="h-[300px] w-full object-cover object-center md:h-[380px]"
                />
              </TiltCard>
            </div>
          </Parallax>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-b border-[#E4E9EF] bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-24">
          <FadeUp className="mx-auto max-w-2xl text-center">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#1F5FD8]">Pricing</div>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#0B2B33] md:text-4xl">
              Simple plans for teams of every size
            </h2>
            <p className="mt-3 text-[#43535E]">Move up when you're ready. Cancel any time.</p>
          </FadeUp>

          <Stagger className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {plans.map((p) => (
              <StaggerItem key={p.code} className="h-full">
                <div style={{ perspective: "1100px" }} className="h-full">
                  <TiltCard
                    strength={p.popular ? 8 : 5}
                    glare={false}
                    data-testid={`plan-card-${p.code}`}
                    className={`relative flex h-full flex-col rounded-2xl border bg-white p-6 ${
                      p.popular
                        ? "border-[#1F5FD8] hs-depth-shadow hs-ring-sheen"
                        : "border-[#E4E9EF] hs-card-shadow"
                    }`}
                  >
                    {p.popular ? (
                      <div
                        className="absolute -top-3 left-1/2 hs-tag"
                        style={{ transform: "translate(-50%, 0) translateZ(40px)" }}
                      >
                        Most popular
                      </div>
                    ) : null}
                    <div style={{ transform: "translateZ(24px)" }}>
                      <div className="text-sm font-semibold text-[#0B2B33]">{p.name}</div>
                      <div className="mt-3 flex items-baseline gap-1">
                        <span className="font-display text-4xl font-bold text-[#0B2B33]">
                          {p.price === null ? "Custom" : `$${p.price}`}
                        </span>
                        {p.price !== null ? <span className="text-sm text-[#8A97A0]">/mo</span> : null}
                      </div>
                      <p className="mt-2 text-sm text-[#43535E]">{TAGLINES[p.code] || ""}</p>
                    </div>
                    <ul className="mt-6 flex-1 space-y-2 text-sm text-[#43535E]" style={{ transform: "translateZ(12px)" }}>
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#16A46B]" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/signup"
                      data-testid={`plan-cta-${p.code}`}
                      style={{ transform: "translateZ(30px)" }}
                      className={`mt-6 inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                        p.popular
                          ? "bg-[#1F5FD8] text-white hover:bg-[#1747B0]"
                          : "border border-[#E4E9EF] text-[#0B2B33] hover:bg-[#F6F8FA]"
                      }`}
                    >
                      {p.price === null ? "Contact sales" : "Start free trial"}
                    </Link>
                  </TiltCard>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* CTA band */}
      <section id="customers" className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <ScrollLift distance={70}>
          <div className="overflow-hidden rounded-3xl border border-[#E4E9EF] bg-white hs-depth-shadow hs-edge-light">
            <div className="grid md:grid-cols-[1.2fr_1fr]">
              <div className="p-10 md:p-14">
                <div className="mb-4 inline-flex items-center gap-2 text-[#1F5FD8]">
                  <MessagesSquare className="h-5 w-5" />
                  <span className="text-xs font-semibold uppercase tracking-wider">Ready when you are</span>
                </div>
                <h2 className="font-display text-3xl font-bold tracking-tight text-[#0B2B33] md:text-4xl">
                  Give your team a calmer place to plan.
                </h2>
                <p className="mt-3 text-[#43535E]">Sign up in under a minute — no card required.</p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Magnetic>
                    <Link to="/signup" data-testid="footer-cta-primary" className="hs-btn-primary inline-flex items-center gap-2">
                      Start free trial <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Magnetic>
                  <Link to="/login" data-testid="footer-cta-secondary" className="hs-underline-cta">Sign in</Link>
                </div>
              </div>
              <img
                src={teamPhoto}
                alt=""
                width={1200}
                height={800}
                loading="lazy"
                className="hidden h-full min-h-[320px] w-full object-cover md:block"
              />
            </div>
          </div>
        </ScrollLift>
      </section>

      <MarketingFooter />
    </div>
  );
}

function MockDashboard() {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div className="rounded-xl border border-[#E4E9EF] p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Scheduled this week</div>
        <div className="mt-2 font-display text-2xl font-bold text-[#0B2B33]">
          <CountUp to={28} />
        </div>
        <div className="mt-1 text-xs text-[#16A46B]">+12% vs. last week</div>
      </div>

      <div className="rounded-xl border border-[#E4E9EF] p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Engagements</div>
        <div className="mt-2 font-display text-2xl font-bold text-[#0B2B33]">14.2k</div>
        <div className="mt-1 flex items-end gap-1">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="flex h-6 w-1.5 items-end rounded-full bg-[#EEF3FB]">
              {/* Grows from a visible baseline rather than from nothing, so a
                  browser that never runs the animation still shows a chart. */}
              <motion.div
                className="w-full rounded-full bg-[#1F5FD8]"
                initial={{ height: "20%" }}
                animate={{ height: `${20 + ((i * 7) % 80)}%` }}
                transition={{ duration: 0.7, delay: 0.4 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-[#E4E9EF] p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Awaiting review</div>
        <div className="mt-2 flex items-center gap-3">
          <div className="font-display text-2xl font-bold text-[#0B2B33]">3</div>
          <div className="flex -space-x-2">
            {["A", "M", "T"].map((c) => (
              <div key={c} className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-[#EEF3FB] text-xs font-bold text-[#1F5FD8]">{c}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="md:col-span-3 rounded-xl border border-[#E4E9EF] p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-[#0B2B33]">This week</div>
          <div className="flex gap-1">
            {["IG", "LI", "FB", "X"].map((n) => (
              <span key={n} className="hs-chip text-[10px]">{n}</span>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="rounded-lg bg-[#F6F8FA] p-2">
              <div className="text-[10px] font-semibold text-[#8A97A0]">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i]}</div>
              <div className="mt-2 space-y-1">
                {i % 2 === 0 ? <div className="h-4 rounded bg-[#1F5FD8]/15"><div className="h-full w-3/5 rounded bg-[#1F5FD8]" /></div> : null}
                {i % 3 === 0 ? <div className="h-4 rounded bg-[#16A46B]/15"><div className="h-full w-4/5 rounded bg-[#16A46B]" /></div> : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
